import type React from 'react';
import { FormattedMessage, IntlProvider, useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';

import { locales } from 'app/locales';
import { LANGUAGE_COOKIE_NAME } from 'state/language';
import { getStorage } from 'utils/storage';

import Typography from 'app/ui/components/atoms/typography';
import HtmlTitle from 'app/ui/components/atoms/html-title';
import HyroxLogo from 'app/ui/components/atoms/icons/hyrox-logo';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colors.black};
  padding: 0 ${({ theme }) => theme.spacing(9)};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
  max-width: 38rem;
  gap: 2rem;

  h3 {
    text-align: center;
    line-height: 1em;
  }

  p {
    text-align: center;
  }
`;

const Logo = styled(HyroxLogo)`
  width: 100%;
  max-width: 10rem;

  ${({ theme: { media } }) => `
    ${media.large(
      () => `
      max-width: 13rem;
      `,
    )}
  `}
`;

// The app's language lives in state we deliberately do not mount here, so read the
// persisted preference straight from storage and fall back to the browser's language.
export const resolveMaintenanceLocale = (): string => {
  const candidates = [getStorage(LANGUAGE_COOKIE_NAME), navigator.language, navigator.language?.split('-')[0]];

  for (const candidate of candidates) {
    const code = candidate?.toLowerCase();

    if (code && locales[code]) {
      return code;
    }
  }

  return 'en';
};

const MaintenanceContent: React.FC = () => {
  const theme = useTheme();
  const intl = useIntl();

  return (
    <Wrapper>
      <HtmlTitle title={intl.formatMessage({ id: 'maintenance.pageTitle' })} />
      <Content>
        <Logo color={theme.colors.white} />
        <Typography variant="h3">
          <FormattedMessage id="maintenance.title" />
        </Typography>
        <Typography variant="body1" color={theme.colors.knop}>
          <FormattedMessage id="maintenance.message" />
        </Typography>
      </Content>
    </Wrapper>
  );
};

// IntlProvider needs no other provider, so the page can be translated without
// mounting the state, services or router trees that maintenance mode shuts off.
const Maintenance: React.FC = () => {
  const locale = resolveMaintenanceLocale();

  return (
    <IntlProvider defaultLocale="en" locale={locale} messages={locales[locale]}>
      <MaintenanceContent />
    </IntlProvider>
  );
};

export default Maintenance;
